import { Translation } from 'components/Translations/Translation'
import styles from '../multiSelect.module.scss'
import { OptionsGroup } from './OptionsGroup'
import { Filter } from './Filter'
import { SelectAll } from './SelectAll'
import { forwardRef, useCallback, useState } from 'react'
import Spinner, { ClipSpinner, OverlaySpinner } from 'components/Spinner'

type FilterType = {
	active: boolean
	fetching: boolean
}

type Props = {
	selectedItems: any[]
	filteredItems: any[]
	filterItems(filterValue: string | undefined): Promise<void> | void
	getItemId(item: any): string | number
	getItemText(item: any): string
	fetchingInitialItems?: boolean
	hasMoreItems?: boolean
	onChange(items: any[]): void
	disabled: boolean
	useDebounce?: boolean
}

export const Dropdown = forwardRef((
	{ selectedItems, filteredItems, filterItems, getItemId, getItemText, fetchingInitialItems, hasMoreItems, onChange, disabled, useDebounce }: Props,
	ref: React.ForwardedRef<HTMLDivElement> | undefined
) => {
	const [filter, setFilter] = useState<FilterType>({
		active: false,
		fetching: false
	});

	const hasAnyFilteredOption = filteredItems.length > 0 || fetchingInitialItems || filter.fetching;
	const hasAnyOption = selectedItems.length > 0 || hasAnyFilteredOption;

	const filterCallback = useCallback(
		async (filterValue: string | undefined) => {
			setFilter({
				active: !!filterValue,
				fetching: true
			});
			await filterItems(filterValue || '');
			setFilter({
				active: !!filterValue,
				fetching: false
			})
		},
		[filterItems]
	)

	const onChangeCallback = useCallback(
		(item: any) => {
			let newSelectedItems: any[] = [];

			const isSelected = selectedItems.includes(item);
			if (isSelected) {
				newSelectedItems = selectedItems.filter(selectedItem => selectedItem !== item);
			} else {
				newSelectedItems = [...selectedItems, item];
			}

			onChange(newSelectedItems);
		},
		[onChange, selectedItems]
	)

	return (
		<div className={styles.dropdown}>
			<Filter
				disabled={disabled}
				onChange={filterCallback}
				useDebounce={useDebounce}
			/>
			{hasAnyOption &&
				<div style={{ position: 'relative' }}>
					<SelectAll
						selectedItems={selectedItems}
						filteredItems={filteredItems}
						isFiltered={filter.active}
						onChange={onChange}
						getItemId={getItemId}
						disabled={disabled}
					/>
					{/* selected options */}
					<OptionsGroup
						items={selectedItems}
						getItemId={getItemId}
						getItemText={getItemText}
						onChange={onChangeCallback}
						isSelected
						// keyboardItem={keyboardItem}
						disabled={disabled}
					/>
					{!disabled && <hr />}
					{/* remaining filtered options */}
					<OptionsGroup
						items={filteredItems}
						getItemId={getItemId}
						getItemText={getItemText}
						onChange={onChangeCallback}
						disabled={disabled}
					/>
					{/* more items spinner and ref for IntersectionObserver to observe for fetching more items */}
					{hasMoreItems &&
						<div ref={ref} className={styles.load_more_spinner}>
							<Spinner>
								<ClipSpinner size={20} />
							</Spinner>
						</div>
					}
					{(fetchingInitialItems || filter.fetching) && <OverlaySpinner useBrandColor withBackground />}
				</div>
			}
			{!disabled && !hasMoreItems && !hasAnyFilteredOption &&
				<div className={styles.no_options}>
					<Translation i18n='i18n.label.noOptions' />
				</div>
			}
		</div>
	)
})
